<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import StandardsAccordion from '@/goals/StandardsAccordion.vue'
import type { StandardGroup } from '@/goals/goal_standards'
import { headingsForStandardSetCode } from '@/standards/standard_headings'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'

const props = withDefaults(
  defineProps<{
    groupedStandards?: StandardGroup[]
    isEditLinkShown?: boolean
    isFirstStandardOpen?: boolean
    isAccordionLayout?: boolean
  }>(),
  {
    groupedStandards: () => [],
    isEditLinkShown: false,
    isFirstStandardOpen: true,
    isAccordionLayout: false
  }
)

const ENGLISH_LEARNER_SET_CODE = 'CA-ELD'
const ENGLISH_LEARNER_HEADING = 'English Learner Standards'

const { profileId } = storeToRefs(useContentProfileStore())

function setHeading(setCode: string): string {
  return headingsForStandardSetCode(setCode, { profileId: profileId.value }) ?? setCode
}

function groupHeading(setCode: string): string {
  if (setCode === ENGLISH_LEARNER_SET_CODE) {
    return ENGLISH_LEARNER_HEADING
  }
  return setHeading(setCode)
}

// An accordion-layout page tightens the gap under each set heading, as the page's other headings do.
const setHeadingClasses = computed(() => [
  'mt-9 text-display-sm font-medium leading-tight',
  props.isAccordionLayout ? 'mb-4' : 'mb-6'
])
const ENGLISH_LEARNER_SET_HEADING_CLASSES = 'mb-2.5 mt-5 text-xl font-medium leading-5'
</script>

<template>
  <div data-testid="goal-content-page-standards">
    <div v-for="group in groupedStandards" :key="group.standardSetCode">
      <h2 :class="setHeadingClasses">{{ groupHeading(group.standardSetCode) }}</h2>
      <h3 v-if="group.standardSetCode === ENGLISH_LEARNER_SET_CODE" :class="ENGLISH_LEARNER_SET_HEADING_CLASSES">
        {{ setHeading(group.standardSetCode) }}
      </h3>
      <StandardsAccordion
        :standards="group.standards"
        :is-edit-link-shown="isEditLinkShown"
        :is-first-standard-open="isFirstStandardOpen"
      />
    </div>
  </div>
</template>
